import React, { useState } from "react";
import { streamSSE } from "@/lib/api";
import Markdown from "@/components/Markdown";
import { toast } from "sonner";
import { Beaker, Loader2, Download, Copy, ShieldCheck } from "lucide-react";

const CLASSES = ["I", "IIa", "IIb", "III"];

export default function CerPanel({ project }) {
  const [f, setF] = useState({
    device_name: project?.device_name || "",
    intended_purpose: project?.description || "",
    device_class: "IIa",
    generic_type: "",
    manufacturer: "",
  });
  const [content, setContent] = useState("");
  const [meta, setMeta] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const run = async () => {
    if (!f.device_name.trim() || !f.intended_purpose.trim()) {
      toast.error("Device name + intended purpose required");
      return;
    }
    setStreaming(true);
    setContent("");
    setMeta(null);
    let buf = "";
    try {
      await streamSSE("/cer/generate", { ...f, project_id: project?.id || null }, (frame) => {
        if (frame.trials || frame.maude) setMeta((m) => ({ ...(m || {}), ...frame }));
        if (frame.delta) {
          buf += frame.delta;
          setContent(buf);
        }
        if (frame.error) toast.error(frame.error);
      });
    } catch (e) {
      toast.error(e.message || "CER generation failed");
    } finally {
      setStreaming(false);
    }
  };

  const copy = async () => { await navigator.clipboard.writeText(content); toast.success("CER copied"); };
  const download = () => {
    const blob = new Blob([content], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(f.device_name || "device").replace(/\s+/g, "_")}_CER.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="cer-root" className="max-w-6xl">
      <div className="mb-6">
        <div className="overline mb-2 text-[#2563EB]">CER GENERATOR · EU MDR ANNEX XIV · MEDDEV 2.7/1 REV 4</div>
        <h1 className="font-serif text-3xl font-semibold tracking-tight lg:text-4xl">
          Clinical Evaluation Report, grounded in real evidence.
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-600">
          Qualmitta pulls live studies from ClinicalTrials.gov + post-market signals from MAUDE, then drafts
          your CER section-by-section. Every claim cites an NCT ID or event type — no fabricated evidence.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <div className="border border-slate-200 bg-white p-5 space-y-3">
            <label className="block">
              <div className="overline mb-1 text-slate-500">Device name *</div>
              <input data-testid="cer-device-name" value={f.device_name} onChange={set("device_name")} className="w-full border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900" />
            </label>
            <label className="block">
              <div className="overline mb-1 text-slate-500">Intended purpose *</div>
              <textarea data-testid="cer-intended-purpose" value={f.intended_purpose} onChange={set("intended_purpose")} rows={3} className="w-full border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900" />
            </label>
            <label className="block">
              <div className="overline mb-1 text-slate-500">Generic device type</div>
              <input data-testid="cer-generic-type" value={f.generic_type} onChange={set("generic_type")} placeholder="e.g., continuous glucose monitor, hip implant" className="w-full border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900" />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label>
                <div className="overline mb-1 text-slate-500">EU MDR class</div>
                <select data-testid="cer-class" value={f.device_class} onChange={set("device_class")} className="w-full border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900">
                  {CLASSES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label>
                <div className="overline mb-1 text-slate-500">Manufacturer</div>
                <input data-testid="cer-manufacturer" value={f.manufacturer} onChange={set("manufacturer")} className="w-full border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900" />
              </label>
            </div>
            <button data-testid="cer-run" onClick={run} disabled={streaming} className="mt-3 inline-flex w-full items-center justify-center gap-2 bg-[#2563EB] px-5 py-3 text-sm font-semibold text-white hover:bg-[#1D4ED8] disabled:opacity-40">
              {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Beaker className="h-4 w-4" />}
              {content ? "Regenerate CER" : "Generate CER"}
            </button>
          </div>

          {meta && (
            <div className="mt-4 space-y-2 text-xs">
              {meta.trials && (
                <div className="border border-slate-200 bg-white p-3">
                  <div className="overline mb-1 text-[#2563EB]">CLINICALTRIALS.GOV · {meta.trials.length} studies</div>
                  {meta.trials.slice(0, 5).map((t) => (
                    <div key={t.nct_id} className="border-t border-slate-100 py-1.5 text-[11px]">
                      <span className="font-mono text-[10px] text-slate-500">{t.nct_id}</span> · <span className="text-slate-700">{(t.title || "").slice(0, 80)}</span>
                    </div>
                  ))}
                </div>
              )}
              {meta.maude && (
                <div className="border border-slate-200 bg-white p-3">
                  <div className="overline mb-1 text-[#2563EB]">MAUDE · {meta.maude.total} events</div>
                  {meta.maude.top_event_types?.map((t) => (
                    <div key={t.event_type} className="text-[11px] text-slate-700">
                      <span className="font-mono">{t.event_type}</span> × {t.count}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="lg:col-span-8">
          <div className="min-h-[420px] border border-slate-200 bg-white">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
              <div className="flex items-center gap-2">
                <Beaker className="h-4 w-4 text-[#2563EB]" />
                <span className="overline text-[#2563EB]">CER DRAFT</span>
                {streaming && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
              </div>
              {content && (
                <div className="flex items-center gap-2">
                  <button data-testid="cer-copy" onClick={copy} className="inline-flex items-center gap-1.5 border border-slate-300 px-2 py-1 text-[11px] text-slate-700 hover:border-slate-400"><Copy className="h-3 w-3" /> Copy</button>
                  <button data-testid="cer-download" onClick={download} className="inline-flex items-center gap-1.5 border border-slate-300 px-2 py-1 text-[11px] text-slate-700 hover:border-slate-400"><Download className="h-3 w-3" /> Markdown</button>
                </div>
              )}
            </div>
            <div data-testid="cer-output" className="max-h-[75vh] overflow-y-auto p-6">
              {content ? (
                <Markdown>{content}</Markdown>
              ) : (
                <div className="flex h-96 flex-col items-center justify-center text-center text-slate-500">
                  <ShieldCheck className="mb-3 h-6 w-6 text-[#2563EB]" strokeWidth={1.5} />
                  <div className="text-sm">Fill the form on the left and hit <b>Generate CER</b>.</div>
                  <div className="mt-1 text-xs text-slate-400">MEDDEV 2.7/1 Rev 4 structure, grounded in live ClinicalTrials.gov + MAUDE.</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
